import { useState, useCallback, useEffect } from 'react'
import { listAccounts, listWatched, logout, startWatcher, stopWatcher } from '../../../api/telegramApi'
import { type TelegramAccount, type WatchedChat } from '../types'

interface UseTelegramAccountsReturn {
    // Accounts data
    accounts: TelegramAccount[]
    watchedMap: Record<string, WatchedChat[]>
    loading: boolean
    loggingOutPhone: string | null
    watcherActionPhone: string | null

    // Alerts
    message: string | null
    error: string | null
    setMessage: (message: string | null) => void
    setError: (error: string | null) => void
    resetAlerts: () => void

    // Actions
    fetchAccounts: () => Promise<void>
    handleLogout: (phone: string) => Promise<void>
    handleStartWatcher: (phone: string) => Promise<void>
    handleStopWatcher: (phone: string) => Promise<void>
    updateWatchedMap: (phone: string, items: WatchedChat[]) => void
}

export function useTelegramAccounts(): UseTelegramAccountsReturn {
    const [accounts, setAccounts] = useState<TelegramAccount[]>([])
    const [watchedMap, setWatchedMap] = useState<Record<string, WatchedChat[]>>({})
    const [loading, setLoading] = useState(false)
    const [loggingOutPhone, setLoggingOutPhone] = useState<string | null>(null)
    const [watcherActionPhone, setWatcherActionPhone] = useState<string | null>(null)

    // Alerts
    const [message, setMessage] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)

    const resetAlerts = useCallback(() => {
        setMessage(null)
        setError(null)
    }, [])

    const fetchAccounts = useCallback(async () => {
        setLoading(true)
        try {
            const rows = await listAccounts()
            // Sort: active accounts first
            const sortedRows = rows.sort((a, b) => {
                if (a.status === 'active' && b.status !== 'active') return -1
                if (a.status !== 'active' && b.status === 'active') return 1
                return 0
            })
            setAccounts(sortedRows)
            // Fetch watched for each account
            const map: Record<string, WatchedChat[]> = {}
            for (const a of sortedRows) {
                try {
                    map[a.phone] = await listWatched(a.phone)
                } catch {
                    map[a.phone] = []
                }
            }
            setWatchedMap(map)
        } catch (e: any) {
            setError(e?.message || 'Failed to load accounts')
        } finally {
            setLoading(false)
        }
    }, [])

    const handleLogout = useCallback(
        async (phone: string) => {
            setLoggingOutPhone(phone)
            try {
                await logout(phone)
                setMessage('Logged out successfully.')
                await fetchAccounts()
            } catch (e: any) {
                setError(e?.message || 'Failed to logout')
            } finally {
                setLoggingOutPhone(null)
            }
        },
        [fetchAccounts]
    )

    const handleStartWatcher = useCallback(
        async (phone: string) => {
            setWatcherActionPhone(phone)
            try {
                await startWatcher(phone)
                setMessage('Watcher started successfully.')
                await fetchAccounts()
            } catch (e: any) {
                setError(e?.message || 'Failed to start watcher')
            } finally {
                setWatcherActionPhone(null)
            }
        },
        [fetchAccounts]
    )

    const handleStopWatcher = useCallback(
        async (phone: string) => {
            setWatcherActionPhone(phone)
            try {
                await stopWatcher(phone)
                setMessage('Watcher stopped successfully.')
                await fetchAccounts()
            } catch (e: any) {
                setError(e?.message || 'Failed to stop watcher')
            } finally {
                setWatcherActionPhone(null)
            }
        },
        [fetchAccounts]
    )

    const updateWatchedMap = useCallback((phone: string, items: WatchedChat[]) => {
        setWatchedMap((m) => ({ ...m, [phone]: items }))
    }, [])

    useEffect(() => {
        void fetchAccounts()
    }, [fetchAccounts])

    return {
        accounts,
        watchedMap,
        loading,
        loggingOutPhone,
        watcherActionPhone,
        message,
        error,
        setMessage,
        setError,
        resetAlerts,
        fetchAccounts,
        handleLogout,
        handleStartWatcher,
        handleStopWatcher,
        updateWatchedMap,
    }
}
